<template>
  <article class="persona-flow-chart">
    <persona-chart-viewport v-if="opened" ref="viewport" :active="active && near">
      <persona-charts :charts="[chart]" :context-key="contextKey" :active="active && near && initialStarted" />
    </persona-chart-viewport>
    <div v-else class="persona-flow-placeholder" aria-label="이어지는 분석 차트">
      <h3>{{ chart.originalname || chart.name || '분석 차트' }}</h3>
      <p>{{ queued ? '앞선 차트를 불러온 뒤 이어서 표시합니다.' : '이 위치로 내려오면 차트를 자동으로 불러옵니다.' }}</p>
    </div>
  </article>
</template>
<script>
import PersonaCharts from './PersonaCharts.vue';
import PersonaChartViewport from './PersonaChartViewport.vue';
import { initialChartQueue } from './initial-chart-queue';

export default {
  name:'PersonaFlowChart',
  components:{PersonaCharts,PersonaChartViewport},
  props:{chart:{type:Object,required:true},contextKey:{type:String,default:''},active:{type:Boolean,default:true}},
  data:()=>({opened:false,near:false,queued:false,initialStarted:false}),
  computed:{
    // Scheduling precheck only. PersonaCharts and the original ticket server
    // continue to enforce the actual account and chart permissions.
    initialLoadAllowed(){
      const state=this.$store && this.$store.state || {},profile=state.profile || {},required=Number(this.chart.authorityLevel);
      return !!(state.isLoggedin && profile.userId && typeof this.chart.url==='string' && this.chart.url && !this.chart.unavailable && Number.isFinite(required) && (Number(state.authorityLevel)||0)>=required);
    }
  },
  watch:{
    active(value){if(!value)this.cancelInitialMount();this.requestInitial();},
    initialLoadAllowed(){if(!this.initialLoadAllowed)this.releaseInitial();this.requestInitial();}
  },
  mounted(){
    this.$el.addEventListener('expert-chart-load',this.onChartLoad);
    this.$el.addEventListener('firstinteractive',this.onInteractive,true);
    window.addEventListener('scroll',this.onInitialPositionChange,{passive:true});
    window.addEventListener('resize',this.onInitialPositionChange);
    if(typeof IntersectionObserver==='undefined'){this.near=true;this.requestInitial();return;}
    this._observer=new IntersectionObserver(this.onIntersection,{rootMargin:'600px 0px',threshold:0});
    this._observer.observe(this.$el);
  },
  beforeDestroy(){
    this._disposed=true;
    this._cancellingInitial=null;
    if(this._observer)this._observer.disconnect();
    this.$el.removeEventListener('expert-chart-load',this.onChartLoad);
    this.$el.removeEventListener('firstinteractive',this.onInteractive,true);
    window.removeEventListener('scroll',this.onInitialPositionChange);
    window.removeEventListener('resize',this.onInitialPositionChange);
    this.releaseInitial();
  },
  methods:{
    onIntersection(entries){
      const entry=entries.find(item=>item.target===this.$el);
      if(!entry)return;
      this.near=entry.isIntersecting;
      this.requestInitial();
    },
    requestInitial(){
      if(this._disposed || this._cancellingInitial)return;
      if(!this.active || !this.near){
        // A queued offscreen item must not start. An already-loading iframe
        // keeps its lease until completion/timeout, avoiding overlapping loads.
        if(this._initialLease && this._initialLease.status==='queued')this.releaseInitial();
        return;
      }
      if(this.initialStarted)return;
      if(!this.initialLoadAllowed){this.opened=true;return;}
      if(this._initialLease){this._initialLease.reprioritize();return;}
      this.queued=true;
      const lease=initialChartQueue.enqueue(entry=>{
        this._initialLease=entry;
        this.queued=false;
        if(this._disposed || !this.active || !this.near || !this.initialLoadAllowed){entry.release();return;}
        this.initialStarted=true;
        this.opened=true;
      },entry=>{
        if(this._initialLease===entry)this._initialLease=null;
        this.queued=false;
      },this.initialPriority);
      if(lease.status!=='done')this._initialLease=lease;
    },
    initialPriority(){
      if(this._disposed || !this.active || !this.near || !this.initialLoadAllowed || this.initialStarted)return null;
      const box=this.$el.getBoundingClientRect();
      const height=window.innerHeight || document.documentElement.clientHeight || 0;
      const visible=box.height>0 && box.bottom>0 && box.top<height;
      // Read from the top of the screen down; outside it, preload the closest.
      const distance=visible?Math.max(0,box.top):Math.max(0,box.top-height,-box.bottom);
      return {visible,distance};
    },
    onInitialPositionChange(){if(this._initialLease)this._initialLease.reprioritize();},
    cancelInitialMount(){
      const lease=this._initialLease;
      if(!lease || lease.status!=='running' || this._cancellingInitial)return;
      // A cached course must not keep another course behind an unfinished first
      // load. Dispose its subtree before releasing the single-start permit.
      // Ordinary viewport exits and already-completed charts never take this path.
      this._cancellingInitial=lease;
      this.opened=false;
      this.initialStarted=false;
      this.$nextTick(()=>{
        if(this._cancellingInitial!==lease)return;
        lease.release();
        this._cancellingInitial=null;
        if(!this._disposed)this.requestInitial();
      });
    },
    releaseInitial(){if(this._initialLease)this._initialLease.release();},
    currentChartEvent(event){
      const viewport=this.$refs.viewport;
      return !!(!this._cancellingInitial && !this._disposed && viewport && event.target && viewport.$el.contains(event.target));
    },
    onChartLoad(event){
      const detail=event.detail;
      if(this.currentChartEvent(event) && detail && detail.view===this.chart.url && ['ready','error','permission'].includes(detail.state))this.releaseInitial();
    },
    onInteractive(event){if(this.currentChartEvent(event))this.releaseInitial();}
  }
};
</script>
<style scoped>
.persona-flow-chart{min-width:0;width:100%;max-width:100%}
.persona-flow-chart + .persona-flow-chart{margin-top:32px;padding-top:24px;border-top:1px solid #dce1e7}
.persona-flow-placeholder{min-height:1000px;padding:0 0 24px;color:#526174;background:linear-gradient(#f0f3f7 0 0) 0 60px/100% 280px no-repeat}
.persona-flow-placeholder h3{margin:0 0 12px;color:#202b3b;font-size:16px;font-weight:650}
.persona-flow-placeholder p{font-size:14px;margin:0}
@media(max-width:620px){.persona-flow-placeholder{min-height:520px}.persona-flow-chart + .persona-flow-chart{margin-top:24px;padding-top:20px}}
</style>
